import type { AccountType } from "shared/types/accounts";
import { ACCOUNT_DISCONNECTED_MSG } from "constants/TooltipMessages";
import type { SelectorAccount, SelectorGroup } from "./types";

type LegacyAccountNameFields = {
  name?: string;
  first_name?: string;
  last_name?: string;
};

function selectorAccountLabel(account: AccountType): string {
  const login = account.loginAlias || account.login;
  if (login) {
    const parts = login.split("->");
    return (parts.length === 2 ? parts[1] : login).trim();
  }
  if (account.displayLogin) return account.displayLogin;

  const legacyAccount = account as AccountType & LegacyAccountNameFields;
  const legacyFullName = [legacyAccount.first_name, legacyAccount.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();

  return legacyFullName || legacyAccount.name || account._id;
}

function selectorAccountGroup(account: AccountType): string | undefined {
  const aliasParts = account.loginAlias?.split("->");
  const aliasGroup =
    aliasParts && aliasParts.length > 1 ? aliasParts[0].trim() : undefined;
  return aliasGroup || account.brandName || undefined;
}

const FACEBOOK_POSTING_SCOPES = [
  "pages_show_list",
  "pages_manage_posts",
  "pages_manage_metadata",
  "pages_read_engagement",
  "pages_read_user_content",
  "business_management",
];

function selectorAccountIsDisconnected(account: AccountType): boolean {
  if (
    account.legit === false ||
    account.status !== "ok" ||
    account.promptApiReconnect
  ) {
    return true;
  }

  const scopes = account.session?.scopes || [];
  const platformType = account.platformType || "IG";

  if (platformType === "FB" && !account.isGroup) {
    return ![
      ...FACEBOOK_POSTING_SCOPES,
      "read_insights",
      "pages_messaging",
    ].every((scope) => scopes.includes(scope));
  }

  if (platformType === "IG") {
    return !(
      account.igTokenAddedAt && scopes.includes("instagram_manage_messages")
    );
  }

  return false;
}

export function accountTypeToSelectorAccount(
  account: AccountType
): SelectorAccount {
  const group = selectorAccountGroup(account);
  const disconnected = selectorAccountIsDisconnected(account);
  return {
    id: account._id,
    label: selectorAccountLabel(account),
    avatarUrl: account.profilePictureUrl,
    platformType: account.platformType || account.platformName,
    color: account.color,
    groupId: group,
    groupLabel: group,
    searchText: [account.login, account.loginAlias, account.displayLogin]
      .filter(Boolean)
      .join(" "),
    disconnected,
    attentionReason: disconnected ? ACCOUNT_DISCONNECTED_MSG : undefined,
  };
}

export function accountTypesToSelectorAccounts(
  accounts: AccountType[]
): SelectorAccount[] {
  return accounts.map(accountTypeToSelectorAccount);
}

export function selectorAccountsToSelectorGroups(
  accounts: SelectorAccount[]
): SelectorGroup[] {
  const groups = new Map<string, SelectorAccount[]>();
  accounts.forEach((account) => {
    if (!account.groupLabel) return;
    const members = groups.get(account.groupLabel) || [];
    members.push(account);
    groups.set(account.groupLabel, members);
  });
  return Array.from(groups.entries()).map(([label, members]) => ({
    id: label,
    label,
    accounts: members,
  }));
}

export function accountTypesToSelectorGroups(
  accounts: AccountType[]
): SelectorGroup[] {
  return selectorAccountsToSelectorGroups(
    accountTypesToSelectorAccounts(accounts)
  );
}
